import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import IkiSymbol from '@/components/IkiSymbol';
import HYPSEYA_ORB from '../orbAsset';
import '@/styles/CreateProfile.css';

const HYPSEYA_WORDMARK = "/hypseya-wordmark.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const intentionLabels = {
  healing: 'Healing',
  focus: 'Focus',
  abundance: 'Abundance',
  love: 'Love',
  growth: 'Growth'
};

function CreateProfile() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    reflection: '',
    profilePhoto: null,
    marketing_consent: false
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [userIntentions, setUserIntentions] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Get user intentions from localStorage
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (userData.intentions && userData.intentions.length > 0) {
      setUserIntentions(userData.intentions);
    }
    
    // Pre-fill name if available
    if (userData.name) {
      setFormData(prev => ({ ...prev, name: userData.name }));
    }
  }, []);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be smaller than 5MB');
        return;
      }
      
      setFormData({ ...formData, profilePhoto: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    try {
      // Get user from localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Update user profile (keep existing intentions from onboarding)
      const updateData = {
        name: formData.name,
        has_completed_onboarding: true,
        marketing_consent: formData.marketing_consent,
        ...(formData.marketing_consent ? { marketing_consent_date: new Date().toISOString() } : {})
      };

      await axios.put(`${API}/users/${userData.email}`, updateData);
      
      // Update localStorage
      const updatedUser = { ...userData, ...updateData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      localStorage.setItem('userName', formData.name);
      
      // Store reflection if provided
      if (formData.reflection.trim()) {
        localStorage.setItem('profileReflection', formData.reflection);
      }
      
      // Store photo preview if provided
      if (photoPreview) {
        localStorage.setItem('profilePhoto', photoPreview);
      }
      
      toast.success('Profile created! Let\'s do a quick check-in.');

      // Navigate to baseline clinical assessment before home
      setTimeout(() => {
        navigate('/assessment?returnTo=/');
      }, 500);
      
    } catch (error) {
      console.error('Error creating profile:', error);
      toast.error('Failed to create profile. Please try again.');
    }
  };

  return (
    <div className="create-profile-container">
      <div className="create-profile-content">
        <div className="profile-logo breathing-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <img src={HYPSEYA_ORB} alt="" style={{ width: '64px', height: 'auto' }} />
          <img src={HYPSEYA_WORDMARK} alt="HYPSEYA" style={{ width: '140px', height: 'auto' }} />
        </div>

        <h1 className="profile-title">Create Your Profile</h1>
        <p className="profile-intro">
          This is your space of becoming.<br />
          Take a moment to bring your presence here.
        </p>

        {/* Show selected intentions from onboarding */}
        {userIntentions.length > 0 && (
          <div className="intentions-confirmation">
            <p className="confirmation-label">Current intentions:</p>
            <div className="intentions-display">
              {userIntentions.map((intentionId, index) => (
                <span key={intentionId} className="intention-tag">
                  {intentionLabels[intentionId] || intentionId}
                  {index < userIntentions.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="profile-form">
          {/* Name Input */}
          <div className="form-group">
            <label htmlFor="name">Name or Soul Name</label>
            <p className="field-hint">How do you want to be called in your inner journey?</p>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter your name..."
              className="profile-input"
            />
          </div>

          {/* Reflection Text Area */}
          <div className="form-group">
            <label htmlFor="reflection">Reflection (optional)</label>
            <p className="field-hint">What are you seeking or healing right now?</p>
            <textarea
              id="reflection"
              value={formData.reflection}
              onChange={(e) => setFormData({ ...formData, reflection: e.target.value })}
              placeholder="Share your thoughts..."
              className="profile-textarea"
              rows={4}
            />
          </div>

          {/* Profile Photo Upload */}
          <div className="form-group">
            <label>Profile Photo (optional)</label>
            <p className="field-hint">Upload or take a photo that feels aligned with your energy.</p>
            
            <div className="photo-upload-area">
              {photoPreview ? (
                <div className="photo-preview">
                  <img src={photoPreview} alt="Profile preview" />
                  <button
                    onClick={() => {
                      setPhotoPreview(null);
                      setFormData({ ...formData, profilePhoto: null });
                    }}
                    className="remove-photo-btn"
                  >
                    <IkiSymbol name="close" size={20} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="upload-btn"
                >
                  <IkiSymbol name="healing" size={32} />
                  <span>Upload Photo</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>

        {/* Marketing consent */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: '12px',
          margin: '24px 0', cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={formData.marketing_consent}
            onChange={e => setFormData({ ...formData, marketing_consent: e.target.checked })}
            style={{ marginTop: '3px', accentColor: '#1E6B68', width: '16px', height: '16px', flexShrink: 0 }}
          />
          <span style={{ fontSize: '0.82rem', color: '#7A9E9B', lineHeight: '1.6' }}>
            I'd like to receive integration tips, practice insights, and HYPSEYA updates.
            <span style={{ display: 'block', marginTop: '2px', fontSize: '0.75rem', color: '#AAB8B6' }}>
              No spam. Unsubscribe any time. RGPD compliant.
            </span>
          </span>
        </label>

        <p className="profile-affirmation">
          You are the creator of this space. Every word, every choice, is part of your alignment.
        </p>

        <button
          onClick={handleSubmit}
          className="profile-submit-btn"
        >
          Continue to Your Journey
        </button>
      </div>
    </div>
  );
}

export default CreateProfile;
